import { createHttpClient } from '@adapters/HttpClient';
import { faArrowRight, faCheckCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ModalCenter } from '@shared/components/ui/ModalCenter';
import type { ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Spinner } from '@shared/components/ui/Spinner';
import { Button } from '@shared/components/ui/button';
import { Badge } from '@shared/components/ui/badge/Badge';
import { useToast } from '@shared/hooks/use-toast';
import { apiClientWH } from '@shared/services';
import { useQueryClient } from '@tanstack/react-query';
import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShipmentStatusMappingAdapter } from '../../adapters/shipmentStatusMappingAdapter';
import {
  UNMAPPED_SHIPMENT_STATUS_MAPPINGS_KEY,
  useUnmappedShipmentStatusMappings,
} from '../../hooks/useUnmappedShipmentStatusMappings';
import type { ShipmentStatus } from '../../models/shipmentStatusMapping.types';
import { ExternalStatusCard } from '../display/ExternalStatusCard';
import { InternalStatusPicker } from '../interaction/InternalStatusPicker';

export interface InteractiveMappingModalRef {
  open: () => void;
}

interface InteractiveMappingModalProps {
  shipmentStatuses: ShipmentStatus[];
  onSuccess?: () => void;
}

export const InteractiveMappingModal = forwardRef<InteractiveMappingModalRef, InteractiveMappingModalProps>(
  ({ shipmentStatuses, onSuccess }, ref) => {
    const { t } = useTranslation('shipmentStatusMappingModule');
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const modalRef = useRef<ModalCenterRef>(null);

    const [isActive, setIsActive] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [selectedStatusId, setSelectedStatusId] = useState('');
    const [mappedCount, setMappedCount] = useState(0);
    const [skippedCount, setSkippedCount] = useState(0);
    const [isSaving, setIsSaving] = useState(false);

    const { data: unmappedList = [], isLoading, isError, refetch } = useUnmappedShipmentStatusMappings(isActive);

    const total = unmappedList.length;
    const isCompleted = !isLoading && !isError && currentIndex >= total && total > 0;
    const currentMapping = !isCompleted && currentIndex < total ? unmappedList[currentIndex] : null;
    const progressPercent =
      total > 0 && currentMapping ? Math.round(((currentIndex + 1) / total) * 100) : isCompleted ? 100 : 0;

    const resetSession = () => {
      setCurrentIndex(0);
      setSelectedStatusId('');
      setMappedCount(0);
      setSkippedCount(0);
      setIsSaving(false);
    };

    const handleClose = useCallback(() => {
      setIsActive(false);
      modalRef.current?.closeModal();
    }, []);

    useImperativeHandle(ref, () => ({
      open: () => {
        resetSession();
        setIsActive(true);
        modalRef.current?.openModal();
      },
    }));

    const advanceToNext = useCallback(() => {
      setSelectedStatusId('');
      setCurrentIndex((prev) => prev + 1);
    }, []);

    const handleSkip = useCallback(() => {
      setSkippedCount((prev) => prev + 1);
      advanceToNext();
    }, [advanceToNext]);

    const handleMap = useCallback(async () => {
      if (!currentMapping || !selectedStatusId) return;

      setIsSaving(true);

      try {
        const httpClient = createHttpClient(apiClientWH);
        const adapter = new ShipmentStatusMappingAdapter(httpClient);

        await adapter.mapShipmentStatus(currentMapping.id, {
          shipment_status_id: Number(selectedStatusId),
        });

        await queryClient.invalidateQueries({ queryKey: ['shipment-status-mappings'] });
        await queryClient.invalidateQueries({ queryKey: UNMAPPED_SHIPMENT_STATUS_MAPPINGS_KEY });
        await queryClient.invalidateQueries({ queryKey: ['shipment-status-mappings-stats-total'] });
        await queryClient.invalidateQueries({ queryKey: ['shipment-status-mappings-stats-unmapped'] });

        setMappedCount((prev) => prev + 1);
        onSuccess?.();
        advanceToNext();
      } catch (error: any) {
        toast({
          title: t('shipmentStatusMapping.messages.saveError'),
          description: error?.response?.data?.message || error?.message,
          variant: 'destructive',
        });
      } finally {
        setIsSaving(false);
      }
    }, [advanceToNext, currentMapping, onSuccess, queryClient, selectedStatusId, t, toast]);

    const handleCloseCompleted = useCallback(() => {
      onSuccess?.();
      handleClose();
    }, [handleClose, onSuccess]);

    const footer =
      currentMapping && !isLoading && !isError && total > 0 && !isCompleted ? (
        <div className="flex items-center justify-between gap-3">
          <Button type="button" variant="outline" onClick={handleClose} disabled={isSaving}>
            {t('shipmentStatusMapping.interactiveMapping.close')}
          </Button>
          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleSkip}
              disabled={isSaving}
              data-testid="interactive-mapping-skip-button"
            >
              {t('shipmentStatusMapping.interactiveMapping.skip')}
              <FontAwesomeIcon icon={faArrowRight} className="ml-2 w-3.5" />
            </Button>
            <Button
              type="button"
              variant="default"
              onClick={handleMap}
              disabled={!selectedStatusId || isSaving}
              className="text-white"
              data-testid="interactive-mapping-map-button"
            >
              {isSaving
                ? t('shipmentStatusMapping.interactiveMapping.saving')
                : t('shipmentStatusMapping.interactiveMapping.mapAndContinue')}
            </Button>
          </div>
        </div>
      ) : undefined;

    const renderContent = () => {
      if (isLoading) {
        return (
          <div className="flex flex-col items-center justify-center gap-3 py-16">
            <Spinner size="md" />
            <p className="text-sm text-subtext">{t('shipmentStatusMapping.interactiveMapping.loading')}</p>
          </div>
        );
      }

      if (isError) {
        return (
          <div className="flex flex-col items-center justify-center gap-4 py-16">
            <p className="text-sm text-error">{t('shipmentStatusMapping.messages.errorLoading')}</p>
            <Button variant="outline" onClick={() => refetch()}>
              {t('shipmentStatusMapping.messages.retry')}
            </Button>
          </div>
        );
      }

      if (total === 0) {
        return (
          <div className="flex flex-col items-center justify-center gap-4 py-12 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-bg-success/20">
              <FontAwesomeIcon icon={faCheckCircle} className="text-2xl text-success" />
            </div>
            <div>
              <p className="font-medium text-title">{t('shipmentStatusMapping.interactiveMapping.noUnmapped')}</p>
              <p className="mt-1 text-sm text-subtext">
                {t('shipmentStatusMapping.interactiveMapping.noUnmappedDescription')}
              </p>
            </div>
            <Button variant="outline" onClick={handleClose}>
              {t('shipmentStatusMapping.interactiveMapping.close')}
            </Button>
          </div>
        );
      }

      if (isCompleted) {
        return (
          <div className="flex flex-col items-center justify-center gap-5 py-10 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-bg-success/20">
              <FontAwesomeIcon icon={faCheckCircle} className="text-2xl text-success" />
            </div>
            <div>
              <p className="font-medium text-title">{t('shipmentStatusMapping.interactiveMapping.completedTitle')}</p>
              <p className="mt-1 text-sm text-subtext">
                {t('shipmentStatusMapping.interactiveMapping.completedDescription')}
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-2">
              <Badge className="bg-bg-success text-success">
                {t('shipmentStatusMapping.interactiveMapping.mappedCount', { count: mappedCount })}
              </Badge>
              <Badge className="bg-card-border text-text">
                {t('shipmentStatusMapping.interactiveMapping.skippedCount', { count: skippedCount })}
              </Badge>
            </div>
            <Button variant="default" onClick={handleCloseCompleted} className="text-white">
              {t('shipmentStatusMapping.interactiveMapping.close')}
            </Button>
          </div>
        );
      }

      if (!currentMapping) return null;

      return (
        <div className="space-y-6">
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-subtext">
                {t('shipmentStatusMapping.interactiveMapping.progress', {
                  current: currentIndex + 1,
                  total,
                })}
              </span>
              <span className="font-medium tabular-nums text-title">{progressPercent}%</span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-card-border">
              <div
                className="h-full rounded-full bg-primary transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          <ExternalStatusCard mapping={currentMapping} />

          <InternalStatusPicker
            shipmentStatuses={shipmentStatuses}
            selectedStatusId={selectedStatusId}
            onSelect={setSelectedStatusId}
            testIdPrefix="interactive-mapping-status"
          />
        </div>
      );
    };

    return (
      <ModalCenter
        ref={modalRef}
        title={t('shipmentStatusMapping.interactiveMapping.title')}
        type="default"
        width="lg"
        footer={footer}
        onCloseModal={() => setIsActive(false)}
        data-testid="interactive-mapping-modal"
      >
        {renderContent()}
      </ModalCenter>
    );
  },
);

InteractiveMappingModal.displayName = 'InteractiveMappingModal';
